/**
 * JudgmentFirstForm — Phase 9B onboarding + Add-Judgment wizard body.
 *
 * 4 required substeps (A/B/C/D) + 1 optional (E). See problem statement
 * "Phase 9B" for the full spec.
 *
 * Consumers:
 *   - `/onboarding` StandardWizard (step "debtor") — sub-stepped
 *   - `/debtors/new` Standard modal — sub-stepped
 *   - `/debtors/new` PRO Advanced Mode — stacked (subIndex === null)
 *
 * The debtor object shape stays wire-compatible with the existing
 * `/api/debtors` payload. Phase 9A judgment fields are populated on
 * `debtor.judgment.*`:
 *   court_level_variant, lien_filed, lien_filed_at, known_real_property_states,
 *   lien_recorded_counties_by_state.
 * (State code is derived from court_level_variant so the legacy
 * `judgment.state` field remains valid for critical-dates + interest.)
 */
import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Info, Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import JeaInput from "@/components/jea/JeaInput";
import JeaSelect, { US_STATES } from "@/components/jea/JeaSelect";
import MoneyInput from "@/components/jea/MoneyInput";
import DropUpload from "@/components/jea/DropUpload";
import {
  useStateVariants,
  variantToStateCode,
  variantToCourtLevelHint,
} from "@/hooks/useJudgmentLaw";
import { NATURE_OF_DEBT, COURT_LEVELS } from "@/lib/pricing";
import RenewalPathwayCard from "@/components/debtors/RenewalPathwayCard";
import { RENEWAL_MODE, resolveRenewalDisplay } from "@/lib/renewalDisplay";

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------
const SubSectionHeader = ({ children, testid }) => (
  <div className="mb-4" data-testid={testid}>
    <div className="text-accent-pink font-semibold text-[13px] tracking-[0.08em] uppercase">
      {children}
    </div>
    <div className="mt-2 h-px bg-accent-pink/20" />
  </div>
);

const Row = ({ children, cols = 2 }) => (
  <div
    className="grid gap-3"
    style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
  >
    {children}
  </div>
);

const HelperCallout = ({ children, tone = "info", testid }) => {
  const tones = {
    info: "border-accent-pink bg-accent-pink/10 text-text-secondary",
    warning: "border-warning bg-warning/10 text-text-secondary",
    danger: "border-danger bg-danger/10 text-white",
  };
  return (
    <div
      className={cn(
        "flex gap-3 rounded-[8px] border-l-2 p-4 text-[13px] leading-5",
        tones[tone] || tones.info
      )}
      data-testid={testid}
    >
      {tone === "danger" ? (
        <AlertTriangle className="text-danger shrink-0 mt-0.5" size={16} />
      ) : (
        <Info
          className={cn("shrink-0 mt-0.5", tone === "warning" ? "text-warning" : "text-accent-pink")}
          size={16}
        />
      )}
      <div className="flex-1">{children}</div>
    </div>
  );
};

const AddressFields = ({ value = {}, onChange, testPrefix }) => (
  <div className="space-y-3">
    <JeaInput
      placeholder="Street"
      value={value.street || ""}
      onChange={(e) => onChange({ ...value, street: e.target.value })}
      data-testid={`${testPrefix}-street`}
    />
    <div className="grid grid-cols-3 gap-3">
      <JeaInput
        placeholder="City"
        value={value.city || ""}
        onChange={(e) => onChange({ ...value, city: e.target.value })}
        data-testid={`${testPrefix}-city`}
      />
      <JeaSelect
        value={value.state || ""}
        onChange={(v) => onChange({ ...value, state: v })}
        placeholder="State"
        data-testid={`${testPrefix}-state`}
        options={US_STATES.map(([code, name]) => ({ value: code, label: `${code} — ${name}` }))}
      />
      <JeaInput
        placeholder="ZIP"
        value={value.zip || ""}
        onChange={(e) =>
          onChange({ ...value, zip: e.target.value.replace(/[^\d-]/g, "").slice(0, 10) })
        }
        data-testid={`${testPrefix}-zip`}
      />
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Substep A — Judgment Essentials
// ---------------------------------------------------------------------------
const EssentialsSubstep = ({ debtor, patch }) => {
  const j = debtor.judgment || {};
  const { options, loading } = useStateVariants();

  const setJudgment = (partial) => patch({ judgment: { ...j, ...partial } });

  const onVariantChange = (variantKey) => {
    setJudgment({
      court_level_variant: variantToCourtLevelHint(variantKey),
      state: variantToStateCode(variantKey),
      variant_key: variantKey, // stored for the insight panel + UI persistence
    });
  };

  const showAZPre2013 = j.variant_key === "AZ_PRE_2013_UNRENEWED";
  const renewalAcknowledged = debtor.az_pre_2013_renewed;

  return (
    <div className="space-y-6">
      <SubSectionHeader testid="substep-a-header">Judgment Essentials</SubSectionHeader>

      <div className="space-y-3">
        <JeaInput
          placeholder="Debtor Full Name (required)"
          value={debtor.full_name || ""}
          onChange={(e) => patch({ full_name: e.target.value })}
          data-testid="judgment-first-full-name"
        />

        <Row>
          <div>
            <label className="jea-caption block mb-1.5">Judgment Entry Date</label>
            <JeaInput
              type="date"
              value={j.date_entered || ""}
              onChange={(e) => setJudgment({ date_entered: e.target.value })}
              data-testid="judgment-first-date-entered"
            />
          </div>
          <div>
            <label className="jea-caption block mb-1.5">Judgment Amount</label>
            <MoneyInput
              placeholder="$0.00"
              valueCents={j.amount_cents || 0}
              onChange={(cents) => setJudgment({ amount_cents: cents })}
              testid="judgment-first-amount"
            />
          </div>
        </Row>

        <div>
          <label className="jea-caption block mb-1.5">State Where Judgment Was Entered</label>
          {loading ? (
            <div className="h-11 rounded-[8px] bg-bg-tertiary animate-pulse" />
          ) : (
            <JeaSelect
              value={j.variant_key || ""}
              onChange={onVariantChange}
              placeholder="Select state / court"
              options={options}
              searchable
              data-testid="judgment-first-state"
            />
          )}
        </div>

        <div>
          <label className="jea-caption block mb-1.5">Nature of Underlying Debt</label>
          <JeaSelect
            value={j.nature_of_debt || ""}
            onChange={(v) => setJudgment({ nature_of_debt: v })}
            placeholder="Nature of Underlying Debt"
            data-testid="judgment-first-nature"
            options={NATURE_OF_DEBT.map((n) => ({ value: n, label: n }))}
          />
        </div>
      </div>

      {showAZPre2013 && (
        <div className="space-y-3" data-testid="az-pre-2013-block">
          <HelperCallout tone="warning" testid="az-pre-2013-hint">
            You selected a pre-2013 Arizona judgment. Was this judgment renewed
            by August 2, 2018?
          </HelperCallout>
          <div className="flex gap-2">
            {[
              { key: "yes", label: "Yes — renewed by Aug 2, 2018" },
              { key: "no", label: "No / Unsure" },
            ].map((opt) => {
              const active = renewalAcknowledged === opt.key;
              return (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => patch({ az_pre_2013_renewed: opt.key })}
                  className={cn(
                    "flex-1 h-11 rounded-[8px] border text-[13px] font-semibold transition-all",
                    active
                      ? "bg-accent-pink text-[#091424] border-transparent"
                      : "bg-bg-primary text-text-secondary border-border-default hover:border-accent-pink/60"
                  )}
                  data-testid={`az-pre-2013-${opt.key}`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          {renewalAcknowledged === "no" && (
            <HelperCallout tone="danger" testid="az-pre-2013-block-continue">
              <div className="font-bold text-white text-[14px] mb-1">
                This judgment has permanently expired.
              </div>
              This judgment has permanently expired and cannot be revived.
              Consult counsel — this cannot be enforced through this platform.
              (Arizona pre-2013 judgments not renewed by Aug 2, 2018 lose all
              enforcement authority.)
            </HelperCallout>
          )}
        </div>
      )}

      <HelperCallout tone="info" testid="essentials-hint">
        We only need enough to start tracking your judgment. Interest, court
        details, and asset data all come next — most fields are optional.
      </HelperCallout>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Substep B — Court + Case Info
// ---------------------------------------------------------------------------
const CourtSubstep = ({ debtor, patch }) => {
  const j = debtor.judgment || {};
  const setJudgment = (partial) => patch({ judgment: { ...j, ...partial } });
  return (
    <div className="space-y-6">
      <SubSectionHeader testid="substep-b-header">Court + Case Info</SubSectionHeader>

      <div className="space-y-3">
        <JeaInput
          placeholder="Case Number"
          value={j.case_number || ""}
          onChange={(e) => setJudgment({ case_number: e.target.value })}
          className="jea-mono"
          data-testid="judgment-first-case-number"
        />
        <JeaInput
          placeholder="Court Name"
          value={j.court_name || ""}
          onChange={(e) => setJudgment({ court_name: e.target.value })}
          data-testid="judgment-first-court-name"
        />
        <Row>
          <div>
            <label className="jea-caption block mb-1.5">Court Level (optional)</label>
            <JeaSelect
              value={j.court_level || ""}
              onChange={(v) => setJudgment({ court_level: v })}
              placeholder="Court Level"
              data-testid="judgment-first-court-level"
              options={COURT_LEVELS.map((l) => ({ value: l, label: l }))}
            />
          </div>
          <div>
            <label className="jea-caption flex items-center gap-1.5 mb-1.5">
              Interest Rate (%) — optional
              <span
                className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] text-text-secondary hover:text-accent-pink cursor-help"
                title="Leave blank to use your state's statutory default. Common statutory rates range 4–12% by jurisdiction."
                data-testid="judgment-first-interest-hint"
              >
                <Info size={12} />
              </span>
            </label>
            <JeaInput
              placeholder="e.g. 5.25 (blank = statutory)"
              value={j.interest_rate_raw ?? (j.interest_rate ? String(j.interest_rate) : "")}
              onChange={(e) => {
                let raw = e.target.value.replace(/[^\d.]/g, "");
                // At most one decimal point
                const firstDot = raw.indexOf(".");
                if (firstDot !== -1) {
                  raw = raw.slice(0, firstDot + 1) + raw.slice(firstDot + 1).replace(/\./g, "");
                }
                const parts = raw.split(".");
                // Allow up to 4 decimal places
                const clean = parts.length > 1 ? `${parts[0]}.${parts[1].slice(0, 4)}` : parts[0];
                // Persist the string so the trailing dot / trailing zeros stay visible while typing.
                const num = parseFloat(clean);
                const persistedNum =
                  clean === "" || clean === "." || !Number.isFinite(num) || num < 0 || num >= 100
                    ? 0
                    : num;
                setJudgment({ interest_rate_raw: clean, interest_rate: persistedNum });
              }}
              type="text"
              inputMode="decimal"
              data-testid="judgment-first-interest-rate"
            />
          </div>
        </Row>
      </div>

      <HelperCallout tone="info">
        The nature of the underlying debt (set on the previous step) drives
        bankruptcy-survivability. Fraud, willful injury, and child-support
        judgments are largely non-dischargeable under 11 U.S.C. § 523.
      </HelperCallout>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Substep C — Debtor Location & Assets (all optional)
// ---------------------------------------------------------------------------
const LocationSubstep = ({ debtor, patch }) => {
  const j = debtor.judgment || {};
  const setJudgment = (partial) => patch({ judgment: { ...j, ...partial } });
  const knownStates = j.known_real_property_states || [];
  const toggleState = (code) => {
    const next = knownStates.includes(code)
      ? knownStates.filter((s) => s !== code)
      : [...knownStates, code];
    setJudgment({ known_real_property_states: next });
  };

  return (
    <div className="space-y-6">
      <SubSectionHeader testid="substep-c-header">Debtor Location &amp; Assets (all optional)</SubSectionHeader>

      <div className="space-y-3">
        <div>
          <label className="jea-caption block mb-1.5">Debtor State of Residence</label>
          <JeaSelect
            value={debtor.residence_state || ""}
            onChange={(v) => patch({ residence_state: v })}
            placeholder="Residence state (optional)"
            data-testid="judgment-first-residence"
            options={US_STATES.map(([code, name]) => ({ value: code, label: `${code} — ${name}` }))}
          />
        </div>

        <div>
          <label className="jea-caption block mb-1.5">
            Known Real Property States{" "}
            <span className="text-text-muted normal-case tracking-normal font-normal">
              — select every state where the debtor may own real property
            </span>
          </label>
          <div className="grid grid-cols-6 gap-1.5" data-testid="known-real-property-grid">
            {US_STATES.map(([code]) => {
              const active = knownStates.includes(code);
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => toggleState(code)}
                  className={cn(
                    "h-9 rounded-[6px] text-[12px] font-bold tracking-[0.06em] border transition-all",
                    active
                      ? "bg-accent-pink text-[#091424] border-transparent"
                      : "bg-bg-primary text-text-secondary border-border-default hover:border-accent-pink/60"
                  )}
                  data-testid={`known-property-${code}`}
                >
                  {code}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="jea-caption block mb-1.5">Last Known Address (optional)</label>
          <AddressFields
            value={debtor.current_address || {}}
            onChange={(v) => patch({ current_address: v })}
            testPrefix="judgment-first-current-address"
          />
        </div>

        <JeaInput
          placeholder="Employer Name (optional)"
          value={debtor.current_employer?.name || ""}
          onChange={(e) =>
            patch({ current_employer: { ...(debtor.current_employer || {}), name: e.target.value } })
          }
          data-testid="judgment-first-employer-name"
        />
      </div>

      <HelperCallout tone="info">
        You can add SSN, DOB, prior addresses, employer history, and asset
        details anytime from the judgment profile. We only need enough to
        start tracking your judgment.
      </HelperCallout>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Substep D — Lien & Renewal Status
// ---------------------------------------------------------------------------
const YesNoToggle = ({ value, onChange, testid, yesLabel = "Yes", noLabel = "No" }) => (
  <div className="flex gap-2" data-testid={testid}>
    {[{ key: true, label: yesLabel }, { key: false, label: noLabel }].map((opt) => {
      const active = value === opt.key;
      return (
        <button
          key={String(opt.key)}
          type="button"
          onClick={() => onChange(opt.key)}
          className={cn(
            "flex-1 h-11 rounded-[8px] border text-[13px] font-semibold transition-all",
            active
              ? "bg-accent-pink text-[#091424] border-transparent"
              : "bg-bg-primary text-text-secondary border-border-default hover:border-accent-pink/60"
          )}
          data-testid={`${testid}-${opt.key ? "yes" : "no"}`}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

const LienSubstep = ({ debtor, patch }) => {
  const j = debtor.judgment || {};
  const setJudgment = (partial) => patch({ judgment: { ...j, ...partial } });
  const lienStates = j.lien_filing_states || [];
  const toggleLienState = (code) => {
    const next = lienStates.includes(code) ? lienStates.filter((s) => s !== code) : [...lienStates, code];
    setJudgment({ lien_filing_states: next });
  };

  const knownStates = j.known_real_property_states || [];
  const txAppearsInKnown = knownStates.includes("TX") || j.state === "TX";
  const renewalSettings = j.renewal_settings || {};

  // iter-40 — resolve the renewal pathway for the selected state so we can
  // conditionally hide the "Has this Judgment Already Been Renewed?" question
  // when the state has NO renewal pathway AND the admin elected to display
  // the neutral copy (display_mode = "no_pathway"). The RenewalPathwayCard
  // below already fetches /api/renewal/config/{state}; we subscribe via its
  // onResolve callback to avoid a duplicate network call.
  const [resolvedCfg, setResolvedCfg] = useState(null);
  const renewalDisplayMode = resolveRenewalDisplay(resolvedCfg);
  const hideRenewedQuestion = renewalDisplayMode === RENEWAL_MODE.NO_PATHWAY;

  // When the state flips to a NO_PATHWAY jurisdiction, force j.renewed to
  // `false` so the hidden field still submits a valid payload (backend treats
  // undefined/null differently from false in validation). Only fires on
  // transition — doesn't thrash if already false.
  useEffect(() => {
    if (hideRenewedQuestion && j.renewed !== false) {
      setJudgment({ renewed: false, renewed_at: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideRenewedQuestion]);

  return (
    <div className="space-y-6">
      <SubSectionHeader testid="substep-d-header">Lien &amp; Renewal Status</SubSectionHeader>

      <div className="space-y-3">
        <label className="jea-caption block">Has a Real Property Lien Been Filed?</label>
        <YesNoToggle
          value={j.lien_filed === true ? true : j.lien_filed === false ? false : undefined}
          onChange={(v) => setJudgment({ lien_filed: v })}
          testid="judgment-first-lien-filed"
        />
        {j.lien_filed === true && (
          <div className="space-y-3 pt-2" data-testid="lien-details">
            <div>
              <label className="jea-caption block mb-1.5">Lien Filing Date</label>
              <JeaInput
                type="date"
                value={j.lien_filed_at || ""}
                onChange={(e) => setJudgment({ lien_filed_at: e.target.value })}
                data-testid="judgment-first-lien-filed-at"
              />
            </div>
            <div>
              <label className="jea-caption block mb-1.5">Lien Filing States</label>
              <div className="grid grid-cols-6 gap-1.5">
                {US_STATES.map(([code]) => {
                  const active = lienStates.includes(code);
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => toggleLienState(code)}
                      className={cn(
                        "h-9 rounded-[6px] text-[12px] font-bold tracking-[0.06em] border transition-all",
                        active
                          ? "bg-accent-pink text-[#091424] border-transparent"
                          : "bg-bg-primary text-text-secondary border-border-default hover:border-accent-pink/60"
                      )}
                      data-testid={`lien-state-${code}`}
                    >
                      {code}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {!hideRenewedQuestion && (
        <div className="space-y-3" data-testid="renewed-question-block">
          <label className="jea-caption block">Has this Judgment Already Been Renewed?</label>
          <YesNoToggle
            value={j.renewed === true ? true : j.renewed === false ? false : undefined}
            onChange={(v) => setJudgment({ renewed: v })}
            testid="judgment-first-renewed"
          />
          {j.renewed === true && (
            <div className="pt-2" data-testid="renewal-details">
              <label className="jea-caption block mb-1.5">Most Recent Renewal / Revival Date</label>
              <JeaInput
                type="date"
                value={j.renewed_at || ""}
                onChange={(e) => setJudgment({ renewed_at: e.target.value })}
                data-testid="judgment-first-renewed-at"
              />
            </div>
          )}
        </div>
      )}

      {/* Phase 9E — Guided state-aware Renewal Pathway */}
      <div className="space-y-2" data-testid="renewal-pathway-block">
        <label className="jea-caption block">Renewal Pathway</label>
        <RenewalPathwayCard
          state={j.state}
          entryDate={j.date_entered}
          priorRenewalRecorded={j.renewed === true}
          debtorId={debtor.id || null}
          renewalDraft={renewalSettings}
          onChange={(v) => setJudgment({ renewal_settings: v })}
          onResolve={setResolvedCfg}
        />
      </div>

      {txAppearsInKnown && (
        <div className="space-y-2" data-testid="tx-county-block">
          <label className="jea-caption block">How many Texas counties have you filed abstracts in?</label>
          <JeaInput
            type="number"
            min="0"
            placeholder="0"
            value={j.tx_abstract_county_count ?? ""}
            onChange={(e) => {
              const n = parseInt(e.target.value || "0", 10);
              setJudgment({ tx_abstract_county_count: Number.isFinite(n) ? n : 0 });
            }}
            data-testid="tx-abstract-count"
          />
          <div className="text-[12px] text-text-muted">
            Texas has no statewide lien — county-by-county abstract filing is required.
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Substep E — Optional Extras (skippable)
// ---------------------------------------------------------------------------
const OptionalSubstep = ({ debtor, patch, onSkipAll }) => {
  const a = debtor.assets || {};
  const setAssets = (partial) => patch({ assets: { ...a, ...partial } });

  return (
    <div className="space-y-6">
      <SubSectionHeader testid="substep-e-header">Optional Extras</SubSectionHeader>

      <div className="flex items-start justify-between gap-4 mb-2">
        <p className="text-text-secondary text-[13px]">
          Skip this entirely if you're just starting — you can add all of this
          later from the judgment profile.
        </p>
        <button
          type="button"
          onClick={onSkipAll}
          className="h-9 px-4 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary transition-colors text-[12px] font-semibold shrink-0"
          data-testid="optional-skip-all"
        >
          Skip for now
        </button>
      </div>

      <div className="space-y-3">
        <JeaInput
          placeholder="Aliases / DBA (comma-separated)"
          value={(debtor.aliases || []).join(", ")}
          onChange={(e) => patch({ aliases: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })}
          data-testid="judgment-first-aliases"
        />
        <Row>
          <JeaInput
            type="date"
            placeholder="Date of Birth"
            value={debtor.dob || ""}
            onChange={(e) => patch({ dob: e.target.value })}
            data-testid="judgment-first-dob"
          />
          <JeaInput
            placeholder="SSN last 4"
            value={debtor.ssn_last4 || ""}
            onChange={(e) => patch({ ssn_last4: e.target.value.replace(/\D/g, "").slice(0, 4) })}
            data-testid="judgment-first-ssn"
          />
        </Row>
      </div>

      <div>
        <div className="jea-caption mb-2">Prior Addresses</div>
        {(debtor.prior_addresses || []).map((addr, i) => (
          <div key={i} className="mb-3">
            <AddressFields
              value={addr}
              onChange={(v) => {
                const next = [...(debtor.prior_addresses || [])];
                next[i] = v;
                patch({ prior_addresses: next });
              }}
              testPrefix={`prior-address-${i}`}
            />
          </div>
        ))}
        <button
          type="button"
          onClick={() => patch({ prior_addresses: [...(debtor.prior_addresses || []), {}] })}
          className="text-accent-pink hover:text-accent-pink-light text-[13px] font-semibold inline-flex items-center gap-1"
          data-testid="add-prior-address"
        >
          <Plus size={14} /> Add Another Address
        </button>
      </div>

      <div>
        <div className="jea-caption mb-2">Known Assets</div>
        {(a.real_property || []).map((rp, i) => (
          <div key={i} className="grid grid-cols-[1fr_180px_36px] gap-2 items-center mb-2">
            <JeaInput
              placeholder="Property Address"
              value={rp.address || ""}
              onChange={(e) => {
                const next = [...a.real_property];
                next[i] = { ...next[i], address: e.target.value };
                setAssets({ real_property: next });
              }}
            />
            <MoneyInput
              placeholder="$0.00"
              valueCents={rp.estimated_value_cents || 0}
              onChange={(cents) => {
                const next = [...a.real_property];
                next[i] = { ...next[i], estimated_value_cents: cents };
                setAssets({ real_property: next });
              }}
              testid={`real-property-value-${i}`}
            />
            <button
              type="button"
              onClick={() => {
                const next = [...a.real_property];
                next.splice(i, 1);
                setAssets({ real_property: next });
              }}
              className="p-2 rounded text-text-secondary hover:text-danger"
            >
              <X size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() =>
            setAssets({
              real_property: [
                ...(a.real_property || []),
                { address: "", estimated_value_cents: 0 },
              ],
            })
          }
          className="text-accent-pink hover:text-accent-pink-light text-[13px] font-semibold inline-flex items-center gap-1"
          data-testid="add-real-property"
        >
          <Plus size={14} /> Add Property
        </button>
      </div>

      <div>
        <div className="jea-caption mb-2">Documents</div>
        <DropUpload
          documents={debtor.documents || []}
          onChange={(docs) => patch({ documents: docs })}
          testid="judgment-first-documents"
        />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------
export const JUDGMENT_FIRST_SUBSTEPS = [
  { key: "essentials", label: "Judgment", required: true, Component: EssentialsSubstep },
  { key: "court", label: "Court + Case", required: true, Component: CourtSubstep },
  { key: "location", label: "Location & Assets", required: false, Component: LocationSubstep },
  { key: "lien", label: "Lien & Renewal", required: true, Component: LienSubstep },
  { key: "optional", label: "Optional Extras", required: false, Component: OptionalSubstep },
];

/**
 * Validators are per-substep AND for the overall "ready to save" state.
 * A payload is "ready to save" when substeps A, B, D are fully valid (C is
 * optional and doesn't gate progress). AZ pre-2013 unrenewed hard-blocks.
 */
export const JUDGMENT_FIRST_VALIDATORS = {
  essentials: (debtor) => {
    const j = debtor.judgment || {};
    if (!(debtor.full_name || "").trim()) return false;
    if (!j.date_entered) return false;
    if (!j.variant_key) return false;
    if ((j.amount_cents || 0) <= 0) return false;
    if (!j.nature_of_debt) return false;
    if (j.variant_key === "AZ_PRE_2013_UNRENEWED" && debtor.az_pre_2013_renewed !== "yes") {
      return false; // block continue
    }
    return true;
  },
  court: (debtor) => {
    const j = debtor.judgment || {};
    return !!((j.case_number || "").trim() && (j.court_name || "").trim());
  },
  location: () => true, // fully optional
  lien: (debtor) => {
    const j = debtor.judgment || {};
    if (typeof j.lien_filed !== "boolean") return false;
    if (typeof j.renewed !== "boolean") return false;
    if (j.lien_filed === true && !j.lien_filed_at) return false;
    if (j.renewed === true && !j.renewed_at) return false;
    return true;
  },
  optional: () => true,
};

export const JUDGMENT_FIRST_HARD_BLOCK = (debtor) => {
  const j = debtor.judgment || {};
  return j.variant_key === "AZ_PRE_2013_UNRENEWED" && debtor.az_pre_2013_renewed === "no";
};

export const isJudgmentFirstReady = (debtor) =>
  JUDGMENT_FIRST_VALIDATORS.essentials(debtor) &&
  JUDGMENT_FIRST_VALIDATORS.court(debtor) &&
  JUDGMENT_FIRST_VALIDATORS.lien(debtor) &&
  !JUDGMENT_FIRST_HARD_BLOCK(debtor);

/**
 * Sub-step pill row for the wizard.
 */
export const JudgmentFirstPills = ({ index, onSelect }) => (
  <div className="flex flex-wrap gap-1 mb-6" data-testid="judgment-first-pills">
    {JUDGMENT_FIRST_SUBSTEPS.map((s, i) => (
      <button
        key={s.key}
        type="button"
        onClick={() => onSelect && onSelect(i)}
        className={cn(
          "h-8 px-3 rounded-full text-[11px] tracking-[0.06em] uppercase font-semibold transition-all inline-flex items-center gap-1",
          i === index
            ? "bg-accent-pink text-[#091424]"
            : i < index
            ? "bg-bg-tertiary text-text-secondary hover:text-white"
            : "bg-bg-primary text-text-muted border border-border-default"
        )}
        data-testid={`judgment-first-pill-${i}`}
      >
        {s.label}
        {!s.required && <span className="text-text-muted text-[9px]">(opt)</span>}
      </button>
    ))}
  </div>
);

/**
 * Main renderer. Passes onSkipAll only to the optional substep.
 */
export const JudgmentFirstForm = ({ debtor, patch, subIndex = null, onSkipOptional }) => {
  if (subIndex == null) {
    // Stacked (PRO advanced mode)
    return (
      <div className="space-y-8">
        {JUDGMENT_FIRST_SUBSTEPS.map(({ key, Component }) => (
          <Component
            key={key}
            debtor={debtor}
            patch={patch}
            onSkipAll={onSkipOptional}
          />
        ))}
      </div>
    );
  }
  const { Component } = JUDGMENT_FIRST_SUBSTEPS[subIndex] || JUDGMENT_FIRST_SUBSTEPS[0];
  return <Component debtor={debtor} patch={patch} onSkipAll={onSkipOptional} />;
};

export default JudgmentFirstForm;
